import type { FC } from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Avatar,
  Box,
  Button,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import axios from "axios";
import PropTypes from 'prop-types';
import { toast } from 'react-hot-toast';
import Web3 from 'web3';
import shallow from 'zustand/shallow';

import { getNonce, verifyAddress } from '../../../api/auth';
import NavWalletIcon from '../../../assets/nav-wallet.png';
import { useAnalytics } from '../../../hooks/dataTracking/useAnalytics';
import useTokenBalances from '../../../hooks/useTokenBalances';
import useWeb3Auth from '../../../hooks/useWeb3Auth';
import { IAuthUser } from '../../../interfaces/user';
import { useAuthStore } from '../../../stores/auth';
import { copyTextToClipboard } from '../../../utils/copyToClipboard';
import { Loading } from '../../utils/Loading';

interface AccountPopoverProps {
  anchorEl: null | Element;
  onClose?: () => void;
  open: boolean;
  user?: IAuthUser | null;
}

export const AccountPopover: FC<AccountPopoverProps> = (props) => {
  const { anchorEl, onClose, open, user, ...other } = props;

  const { metamaskAccount, deactivate: disconnectMetamask, connector } = useWeb3Auth();
  const { sendButtonClickTrackingEvent } = useAnalytics();

  const { storedToken, getUser, clearUser } = useAuthStore(
      ({ storedToken, getUser, clearUser }) => ({
        storedToken,
        getUser,
        clearUser,
      }),
      shallow
  );

  const { balances: tokenBalances, loading: tokenBalancesLoading } = useTokenBalances();
  // Added a check to prevent empty objects ({}) from being mapped
  const nextBalances = tokenBalances.filter((balance) => !!balance.address);

  const startAccountVerification = async () => {
    sendButtonClickTrackingEvent('Start connected MetaMask account verification');
  
    const data = await getNonce(metamaskAccount as string);

    if (!data.nonce) return toast.error('Unable to verify account, please try again later.');

    const provider = await connector?.getProvider();
    const web3 = new Web3(provider as any);

    try {
      const signature = await web3.eth.personal.sign(
        `Welcome to Pegaxy Themis | ${data.nonce}`,
        metamaskAccount as string,
        '',
      );

      const tokenData = await verifyAddress(metamaskAccount as string, signature);

      console.log('token data', tokenData);

      if (!tokenData.accessToken) return toast.error('Unable to verify account, please try again later.');

      getUser(tokenData.accessToken);

      toast.success('Account verified!');
    } catch (err) {
      console.log('sign error', err);
      return toast.error('Unable to verify account, please try again later.');
    }
  };

  const unlinkDiscord = async () => {
    try {
      sendButtonClickTrackingEvent('Unlink Discord');
  
      if (!storedToken)
        return toast.error("Account Not Verified cannot Unlink.");

      const { status } = await axios.delete(
          `${process.env.REACT_APP_BASE_API_URL}/auth/discord`,
          { headers: { Authorization: `Bearer ${storedToken}` } }
      );
      if (status >= 200 && status <= 300) {
        getUser(storedToken);
        return toast.success("Successfully unlinked discord");
      }
    } catch (err) {
      console.log("sign error", err);
      return toast.error("Unable to unlink discord, please try again later.");
    }
  };

  const startDiscordVerification = async () => {
    sendButtonClickTrackingEvent('Start Discord verification');

    const data = await getNonce(metamaskAccount as string);

    if (!data.nonce)
      return toast.error("Unable to verify account, please try again later.");

    try {
      if (!storedToken) return toast.error("Account Not Verified cannot Link.");
      const redirectUri = `${process.env.REACT_APP_BASE_API_URL}/auth/discord`;

      window.open(
          `https://discord.com/oauth2/authorize?response_type=code&client_id=948065229521178694&redirect_uri=${redirectUri}&scope=identify&state=` +
          storedToken,
          "_self"
      );
      toast.success("New Window Opened for Login");
    } catch (err) {
      console.log("sign error", err);
      return toast.error("Unable to verify account, please try again later.");
    }
  };

  const handleDisconnect = async () => {
    clearUser();
    sendButtonClickTrackingEvent('Disconnect MetaMask wallet');

    await disconnectMetamask();
  };

  const getAddress = (isLong: boolean) => {
    const address = user?.publicAddress || metamaskAccount || '';

    if (isLong) return address;

    return `${address.substring(0, 6)}...${address.substring(address.length - 6)}`;
  };

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{
        horizontal: 'center',
        vertical: 'bottom',
      }}
      keepMounted
      onClose={onClose}
      open={open}
      PaperProps={{ sx: { width: 300 } }}
      transitionDuration={300}
      sx={{
        top: 40,
      }}
      {...other}
    >
      <Box
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          display: 'flex',
        }}
      >
        <Avatar
          src={''}
          sx={{
            backgroundColor: '#5048E5',
            height: 40,
            width: 40,
            p: 0.75,
          }}
        >
          <img style={{ maxWidth: '100%', marginLeft: 2 }} src={NavWalletIcon} alt="" />
        </Avatar>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            sx={{
              ml: 1,
            }}
          >
            {!!user?.publicAddress || !!metamaskAccount ? (
              <Tooltip title={'Copy Address'} placement={'top'}>
                <Button
                  color={'inherit'}
                  endIcon={<ContentCopyIcon fontSize={'small'} />}
                  sx={{ mr: 1 }}
                  onClick={() => {
                    copyTextToClipboard(getAddress(true));
                    sendButtonClickTrackingEvent('Copy wallet address');
                  }}
                >
                  {`${getAddress(false)}`}
                </Button>
              </Tooltip>
            ) : null}
          </Box>
        </Box>
      </Box>
      { !user ? (
          // User not authed - Display 'Verify Account' Button
          <>
            <Divider />
            <Box sx={{ p: 2, pt: 0 }}>
              <Button
                  color="primary"
                  fullWidth
                  sx={{ mt: 2 }}
                  variant="contained"
                  onClick={() => startAccountVerification()}
              >
                {"Verify Account"}
              </Button>
            </Box>
          </>
      ) : !user.discordID ? (
          // User authed, but no linked Discord - Display 'Link Discord' Button
          <>
            <Divider />
            <Box sx={{ p: 2, pt: 0 }}>
              <Button
                  color="primary"
                  fullWidth
                  sx={{ mt: 2 }}
                  variant="contained"
                  onClick={() => startDiscordVerification()}
              >
                {"Link Discord"}
              </Button>
            </Box>
          </>
      ) : (
          // User is currently linked to a discord
          <>
            <Divider />
            <Box sx={{ p: 2, pt: 0 }}>
              <Button
                  color="primary"
                  fullWidth
                  sx={{ mt: 2 }}
                  variant="contained"
                  onClick={() => unlinkDiscord()}
              >
                { "Unlink Discord" }
              </Button>
            </Box>
          </>
      )}
      <Divider />
      <Box>
        {!tokenBalancesLoading && tokenBalances ? (
          <List sx={{ width: '100%' }}>
            {nextBalances.map((tokenBalance, idx) => (
              <ListItem
                key={idx}
                secondaryAction={
                  <Box sx={{ textAlign: 'end' }}>
                    {tokenBalance.abbr === 'PGX' ? (
                      <>
                        <Typography>{Number(user?.walletPgx || 0).toLocaleString()}</Typography>
                        <Typography color="text.secondary" variant="caption">
                          +{Number(user?.lpPgx || 0).toLocaleString()} LP
                        </Typography>
                      </>
                    ) : (
                      <Typography>{Number(tokenBalance.value).toLocaleString()}</Typography>
                    )}
                  </Box>
                }
              >
                <ListItemAvatar>{tokenBalance.icon}</ListItemAvatar>
                <ListItemText primary={tokenBalance.abbr} secondary={tokenBalance.name} />
              </ListItem>
            ))}
          </List>
        ) : (
          <Box
            sx={{
              minHeight: 200,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Loading />
          </Box>
        )}
      </Box>
      <Divider />
      <Box sx={{ p: 2, pt: 0 }}>
        <Button color="primary" fullWidth sx={{ mt: 2 }} variant="contained" onClick={() => handleDisconnect()}>
          {'Disconnect'}
        </Button>
      </Box>
    </Popover>
  );
};

AccountPopover.propTypes = {
  anchorEl: PropTypes.any,
  onClose: PropTypes.func,
  open: PropTypes.bool.isRequired,
};
